import React, { useState } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { WhatsAppFab } from "../components/WhatsAppFab";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export function About() {
  const { lang } = useApp();
  return (
    <div className={lang === "hi" ? "lang-hi" : ""}>
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-14">
        <h1 className="font-heading text-4xl font-bold">{lang==="hi"?"हमारे बारे में":"About Us"}</h1>
        <img src="https://images.unsplash.com/photo-1629909614456-6b1c5c94cecc?w=1200" className="rounded-3xl mt-6 aspect-[16/8] object-cover w-full card-shadow" alt=""/>
        <p className="text-slate-600 mt-6 leading-relaxed">
          {lang==="hi"
            ? "AASAN OPD भारत में स्वास्थ्य सेवाओं तक आसान पहुँच बनाने के मिशन पर है। हमारा प्लेटफ़ॉर्म आपको एक जगह से अल्ट्रासाउंड, ओपीडी, एक्स-रे, सीटी, एमआरआई और दवाइयों की सुविधा प्रदान करता है।"
            : "AASAN OPD is on a mission to make quality healthcare accessible across India. Our platform brings ultrasound, OPD, X-ray, CT, MRI, and medicines together in one place."}
        </p>
      </div>
      <WhatsAppFab />
      <Footer />
    </div>
  );
}

export function Contact() {
  const { lang } = useApp();
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const send = async () => {
    if (!f.name || !f.message) return toast.error("Name & message required");
    await api.post("/contact", f);
    toast.success(lang==="hi"?"संदेश भेज दिया गया":"Message sent");
    setF({ name: "", email: "", phone: "", message: "" });
  };
  return (
    <div className={lang === "hi" ? "lang-hi" : ""}>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-14 grid md:grid-cols-2 gap-10">
        <div>
          <h1 className="font-heading text-4xl font-bold">{lang==="hi"?"संपर्क करें":"Contact Us"}</h1>
          <p className="text-slate-600 mt-2">{lang==="hi"?"हम आपकी सहायता के लिए हमेशा तैयार हैं।":"We're here to help."}</p>
          <div className="mt-6 space-y-4 text-sm">
            <div className="flex items-center gap-3"><MapPin className="text-blue-600"/> Main Road, Begusarai, Bihar 851101</div>
            <div className="flex items-center gap-3"><Phone className="text-emerald-600"/> +91 9798203741</div>
            <div className="flex items-center gap-3"><Mail className="text-violet-600"/> care@aasanopd.in</div>
            <a href="https://wa.me/919798203741" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-full bg-emerald-500 text-white"><MessageCircle size={16}/> WhatsApp Us</a>
          </div>
          <iframe title="map" className="w-full h-64 rounded-2xl mt-6 border" src="https://www.google.com/maps?q=Begusarai,Bihar&output=embed"/>
        </div>
        <div className="bg-white rounded-3xl card-shadow p-6 space-y-3">
          <input placeholder={lang==="hi"?"नाम":"Name"} value={f.name} onChange={e => setF({...f, name: e.target.value})} className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
          <input placeholder="Email" value={f.email} onChange={e => setF({...f, email: e.target.value})} className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
          <input placeholder="Phone" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
          <textarea rows={5} placeholder={lang==="hi"?"संदेश":"Message"} value={f.message} onChange={e => setF({...f, message: e.target.value})} className="w-full rounded-xl bg-slate-50 border px-4 py-3"/>
          <button onClick={send} data-testid="contact-send-btn" className="btn-primary w-full">Send</button>
        </div>
      </div>
      <WhatsAppFab />
      <Footer />
    </div>
  );
}

export function LegalPage({ kind }) {
  const { lang } = useApp();
  const map = {
    terms: { hi: "शर्तें और नियम", en: "Terms & Conditions" },
    privacy: { hi: "गोपनीयता नीति", en: "Privacy Policy" },
    refund: { hi: "रिफ़ंड नीति", en: "Refund Policy" },
    help: { hi: "मदद और सहायता", en: "Help & Support" },
  };
  const title = map[kind][lang];
  return (
    <div className={lang === "hi" ? "lang-hi" : ""}>
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-14 prose prose-slate">
        <h1 className="font-heading text-4xl font-bold">{title}</h1>
        <p className="text-slate-600 mt-4 leading-relaxed">
          {lang==="hi"
            ? "यह पृष्ठ आपके अधिकारों और सेवाओं से जुड़ी शर्तें स्पष्ट करने के लिए है। कृपया किसी भी प्रश्न के लिए हमसे संपर्क करें।"
            : "This page outlines your rights and terms for our services. Please contact us for any questions."}
        </p>
        <ul className="mt-4 space-y-2 text-slate-600 list-disc pl-5">
          <li>{lang==="hi"?"बुकिंग रद्द 24 घंटे पहले तक।":"Bookings can be cancelled up to 24 hours before slot."}</li>
          <li>{lang==="hi"?"डेटा गोपनीयता का पूर्ण पालन।":"We adhere to strict data privacy standards."}</li>
          <li>{lang==="hi"?"रिफ़ंड 5–7 कार्यदिवसों में।":"Refunds processed within 5–7 business days."}</li>
        </ul>
      </div>
      <Footer />
    </div>
  );
}
